// Canonical topic-name builders for every Realtime channel in the system.
// One file, all topics. Format: `<scope>:<entity>:<id>[:<aspect>]:<kind>`
// where `<kind>` ∈ `snapshot | ledger | presence`. See ADR 009 for the spec.
//
// Publisher AND subscriber call into the same builder — string-literal topic
// names anywhere else in the codebase are a smell. A planned ESLint rule
// (`@auctogon/realtime/use-topic-builder`) will ban them at the syntax level.

/** Topic builders grouped by parent scope. */
export const topics = {
    lot: (id: string) => ({
        /** `lot:{id}:snapshot` — public lot state (price, status, …). */
        snapshot: () => `lot:${id}:snapshot` as const,
        bids: {
            /** `lot:{id}:bids:ledger` — append-only bid feed for this lot. */
            ledger: () => `lot:${id}:bids:ledger` as const,
            /**
             * `admin:lot:{id}:bids:ledger:{token}` — the obscured admin
             * DE-ANONYMIZED bid ledger for ONE lot (BUG-1467). The per-lot
             * sibling of `auction(...).bids.adminLedger`, and the clerk console
             * bid record's source.
             *
             * WHY PER-LOT. The auction-wide topic's catch-up is capped at the
             * newest 2,000 bids, and the console then filtered that fold to the
             * selected lot — so on a sale past the cap a lot's entire history
             * could be absent and the pane showed "No bids on this lot" beside a
             * workspace showing the hammer price. One lot's bids are bounded and
             * `idx_bids_lot_seq` covers them exactly, so this topic needs no cap
             * rather than a bigger one.
             *
             * Same construction as every admin topic: `admin:` marks privilege,
             * `token` is an HMAC minted server-side
             * (`deriveAdminLotBidLedgerTopic` in `@auctogon/realtime`) under a
             * DISTINCT message prefix, so a lot's admin topic can never collide
             * with its auction's. The client never builds it — it learns the
             * full string from the authenticated catch-up response.
             */
            adminLedger: (token: string) =>
                `admin:lot:${id}:bids:ledger:${token}` as const,
        },
        maxBids: {
            /**
             * `admin:lot:{id}:maxbids:{token}` — the obscured admin MAX-BID
             * stream for ONE lot (BL-1552). The per-lot replacement for
             * `auction(...).maxBids.adminState`, which was auction-wide and
             * uncapped: at 2,500 lots its cold seed was 5.7 MB — the largest
             * single payload on the clerk console — to answer a question about
             * whichever lot the clerk had selected. The auction-wide topic and
             * its deriver are GONE (ADR 044 — Must Consolidate); this is the
             * only admin max-bid channel.
             *
             * Carries per-`max_bids` absolute-state deltas across ALL FIVE
             * `max_bid_status` values — the pre-bid section and the `max $X`
             * chips are both derived from it, so filtering by status or by
             * `placedDuringPreBid` here would hide rows the ladder needs.
             * `selectLotPreBids` still governs which rows render under the
             * Pre-bids heading; that is a membership question about the
             * pre-bidding PHASE, unrelated to this channel's scope.
             *
             * CONFIDENTIAL: the payload carries a bidder's STORED MAXIMUM — the
             * ceiling their proxy will bid to, the single most sensitive number
             * on the clerk's screen — alongside the `customer_id` holding it.
             * Narrowing the channel to one lot narrows the blast radius of a
             * leaked token but emphatically does not make it public. Same
             * posture as its siblings: `admin:` marks privilege; `token` is an
             * HMAC minted server-side (`deriveAdminLotMaxBidsTopic` in
             * `@auctogon/realtime`) — the client NEVER builds this topic, it
             * learns the full string from the authenticated catch-up response.
             * Its HMAC message prefix is DISTINCT from every sibling's, so
             * knowing one channel's token grants none of the others under the
             * one shared secret.
             */
            adminState: (token: string) =>
                `admin:lot:${id}:maxbids:${token}` as const,
        },
    }),
    auction: (id: string) => ({
        /** `auction:{id}:snapshot` — auction-level state (status, pause, increments). */
        snapshot: () => `auction:${id}:snapshot` as const,
        // NO auction-wide bid topics. Both are deleted, for the same reason:
        // their only consumer was the clerk rail's client-side activity fold.
        //   • `admin:auction:{id}:bids:ledger:{token}` (de-anon) — BL-1556, which
        //     also removed its sole minter `deriveAdminBidLedgerTopic`.
        //   • `auction:{id}:bids:ledger` (anon) — BL-1632, the public twin left
        //     behind by that sweep. It broadcast on EVERY bid to zero
        //     subscribers, and was never a durable ledger: `ledger_topic_kind`
        //     has no `auction_bids` member, so it had no `ledger_topic_seq`
        //     counter and no catch-up route (ADR 011 §6 + "Migration from
        //     BL-447 / BL-452", which prescribe exactly this deletion).
        // The PER-LOT siblings are the live ones: `topics.lot(id).bids.ledger`
        // (bidder feed) and `.adminLedger` (the console's bid record, BUG-1467).
        // An auction-wide consumer folds the per-lot topics client-side.
        lots: {
            /**
             * `admin:auction:{id}:lots:state:{token}` — the obscured admin
             * lot-state stream (ADR 042 §§1/6, BL-1251). Carries per-lot
             * absolute-scalar deltas (`lot_state.delta`) for the whole auction
             * on a private, hard-to-guess channel. The `admin:` scope prefix
             * marks privilege; `token` is an HMAC minted server-side
             * (`deriveAdminLotStateTopic` in `@auctogon/realtime`) — the client
             * NEVER builds this topic, it learns the full string from the
             * authenticated catch-up/mint response. Obscurity is the interim
             * access gate until the transport moves to real per-subscriber auth.
             */
            adminState: (token: string) =>
                `admin:auction:${id}:lots:state:${token}` as const,
        },
        registrations: {
            /**
             * `admin:auction:{id}:registrations:{token}` — the obscured admin
             * auction-registrations stream (BL-1262). Carries per-registration
             * absolute-state deltas (`registration.delta`) for the whole auction
             * on a private, hard-to-guess channel, so the clerk console's bidder
             * rail sees a new request the moment it arrives.
             *
             * CONFIDENTIAL: the payload is identifiers-only — it carries NO
             * bidder identity (the rail re-reads the authenticated bidders
             * endpoint for that). Even so, WHICH registrations are moving on an
             * auction is privileged, so this keeps the same posture as
             * `lots.adminState` and `bids.adminLedger`. `admin:` marks privilege;
             * `token` is an HMAC minted server-side
             * (`deriveAdminRegistrationsTopic` in `@auctogon/realtime`) — the
             * client NEVER builds this topic, it learns the full string from the
             * authenticated catch-up response. Obscurity is the interim access
             * gate until the transport moves to real per-subscriber auth.
             * Distinct HMAC message prefix from `lots.adminState` and
             * `bids.adminLedger` so the three admin topics never collide under
             * one secret.
             */
            adminState: (token: string) =>
                `admin:auction:${id}:registrations:${token}` as const,
        },
        // BL-1552 removed `maxBids` from here. The auction-wide obscured admin
        // max-bid topic (`admin:auction:{id}:maxbids:{token}`) cold-seeded 5.7 MB
        // at 2,500 lots to serve a centre panel about ONE selected lot. Its only
        // minter, `deriveAdminMaxBidsTopic`, went with it. The PER-LOT sibling —
        // `topics.lot(id).maxBids.adminState` — is the live one, and is what the
        // console's bid record subscribes to. Same shape as BL-1556's removal of
        // `bids.adminLedger` directly above.
        /**
         * `auction:{id}:presence` — online roster for this auction.
         *
         * BL-1262: bidder-web published on a raw `auction-presence-{id}` literal
         * that predated this builder, and admin-web's subscriber had been deleted,
         * so the builder sat unused while the wire name diverged from it. Both
         * sides now call this builder — the bidder tracker publishes here and the
         * clerk console's roster subscribes here — which restores the ADR 009
         * `<scope>:<entity>:<id>:<kind>` shape (and the `grep ':presence'`
         * contract asserted below) instead of enshrining the dashed legacy name.
         *
         * Presence is the documented public-channel carve-out (`architecture.md`
         * → "No Client-Side DB Access"): client-write by design, roster-only, no
         * payload beyond the tracked identity.
         */
        presence: () => `auction:${id}:presence` as const,
    }),
    user: (id: string) => ({
        registrations: {
            /** `user:{id}:registrations:snapshot` — bid-registration approval state. */
            snapshot: () => `user:${id}:registrations:snapshot` as const,
        },
    }),
    customer: (id: string) => ({
        registrations: {
            /** `customer:{id}:registrations:snapshot` — bid-registration approval
             *  state keyed by `customers.id` (BL-591 / BUG-787). The realtime
             *  listener publishes here; bidder subscribers consume here via
             *  `topicForCustomer` in
             *  `apps/bidder-web/src/lib/bid-registration/bid-registration-access.ts`. */
            snapshot: () => `customer:${id}:registrations:snapshot` as const,
        },
        bidPositions: {
            /** `customer:{id}:bid-positions:snapshot` — the buyer's OWN bidding
             *  position (active max + live-bid flag) per lot, keyed by
             *  `customers.id` (BL-1306).
             *
             *  HINT-ONLY. The broadcast carries `{topicId, version}` and no
             *  payload; the values come back over the AUTHENTICATED catch-up
             *  route, which 403s a `topicId` that isn't the session's customer.
             *  Unlike the sibling `registrations` topic this data is private —
             *  a standing max is the one number that must never reach a
             *  competitor, and `realtime.messages`' SELECT policy does not scope
             *  a subscriber to its own entity (see the BUG-795 migration note).
             *  Never add a payload to this topic. */
            snapshot: () => `customer:${id}:bid-positions:snapshot` as const,
        },
    }),
    tenant: (id: string) => ({
        conversations: {
            /**
             * `admin:tenant:{id}:conversations:delta:{token}` — the obscured,
             * TENANT-scoped admin conversations trigger (BL-1489 AC 6). Carries
             * per-thread identifiers-only deltas (`conversation.delta`) for the
             * whole tenant, so every open clerk console re-reads its shared
             * inbox when a bidder writes, a colleague replies, or a colleague
             * claims a thread read.
             *
             * The ONLY admin topic scoped to a tenant rather than an auction
             * — a bidder's thread spans auctions (one conversation per bidder
             * per tenant, forever), so a per-auction topic cannot express it.
             * Its token is therefore an unrevocable tenant-wide bearer
             * credential (plan §6.1a(b), accepted; narrowed by D15 to
             * identifiers only). `admin:` marks privilege; `token` is an HMAC
             * minted server-side (`deriveAdminConversationsTopic` in
             * `@auctogon/realtime`) under a message prefix distinct from every
             * per-auction admin channel — the client NEVER builds this topic.
             *
             * `delta` in the name records the model: a last-writer-wins trigger
             * with a wall-clock cursor, NOT a versioned snapshot (there is no
             * snapshot table — the 2026-09-04 amendment withdrew it).
             */
            adminDelta: (token: string) =>
                `admin:tenant:${id}:conversations:delta:${token}` as const,
        },
    }),
    /**
     * A bidder's PRIVATE topics (BUG-1938). Both end in an HMAC `token` minted
     * server-side (`deriveCustomerUserPrivateTopics` in `@auctogon/realtime`),
     * because the id alone is not a secret: the presence roster hands every
     * co-bidder's `customer_user.id` to every approved bidder. The client never
     * builds these — it learns both strings from the authenticated
     * `/api/realtime/private-topics` route — and the gateway refuses a
     * `customer-user:` join without the segment. The `<kind>` stays the
     * semantic suffix ahead of the token, as on the obscured `admin:` topics.
     */
    customerUser: (id: string) => ({
        toasts: {
            /** `customer-user:{id}:toasts:ledger:{token}` — append-only server-driven
             *  toast feed for one logged-in customer, keyed by `customer_user.id`
             *  (notifications-refactor C1 / BL-604). Distinct scope from
             *  `customer:` above (which keys on `customers.id`) so the two
             *  identity spaces never collide. The bidder never builds this
             *  topic: it joins the string `/api/realtime/private-topics` returns
             *  (BUG-1938), because the bare id alone is public to co-bidders.
             *
             *  BL-1486 changed the PUBLISHER, not the topic: the retired
             *  `publishCustomerToast` (and its `customer_toasts` table) is gone;
             *  the toast now rides the `customer_in_app_notifications` delivery
             *  record and is broadcast by the shared `toast` realtime leg
             *  post-commit. The topic, the `ledger.appended` event, and the
             *  payload shape are unchanged, which is why `CustomerToastListener`
             *  needed no change. */
            ledger: (token: string) => `customer-user:${id}:toasts:ledger:${token}` as const,
        },
        unreads: {
            /** `customer-user:{id}:unreads:snapshot:{token}` — the two unread counts for
             *  one logged-in customer (BL-1486), keyed by `customer_user.id`.
             *
             *  A SNAPSHOT beside the ledger above, deliberately, and NOT merged
             *  into it: marking a notification read must rebroadcast the counts,
             *  which is an overwrite on a snapshot and an unbounded append on a
             *  ledger. The `<kind>` suffix is semantics, not naming.
             *
             *  Payload-carrying rather than hint-only — a declared deviation
             *  from ADR 011 § 5, accepted by Michael Lawrie 2026-09-03, because
             *  hint-only would mean a catch-up stampede on every broadcast. The
             *  line it does not cross: a DM body never rides any channel. */
            snapshot: (token: string) =>
                `customer-user:${id}:unreads:snapshot:${token}` as const,
        },
    }),
    /**
     * A staff member's PRIVATE topics at one house (BL-2491, ADR 056 — Delivery
     * Record § 9(d)). Staff work at several houses, so the scope names the
     * person AND the house, and each ends in an HMAC `token` minted server-side
     * (`deriveStaffUserPrivateTopics` in `@auctogon/realtime`) over that pair.
     * The client never builds these: it learns the string from the
     * authenticated `/api/realtime/staff-topics` route, and the gateway refuses
     * a `staff-user:` join without the segment.
     */
    staffUser: (id: string) => ({
        tenant: (tenantId: string) => ({
            toasts: {
                /** `staff-user:{id}:tenant:{tenantId}:toasts:ledger:{token}` — the
                 *  person's live mention pop-ups at this house (BL-2473), the staff
                 *  twin of `customerUser(id).toasts.ledger`. Each `ledger.appended`
                 *  is one `staff_in_app_notifications` row of an @Inbox type that
                 *  carries a toast body. Live-only, like the bidder's: no catch-up,
                 *  because a missed pop-up waits in the @Inbox, whose count does
                 *  catch up. Same token as the unreads snapshot below. */
                ledger: (token: string) => `staff-user:${id}:tenant:${tenantId}:toasts:ledger:${token}` as const,
            },
            unreads: {
                /** `staff-user:{id}:tenant:{tenantId}:unreads:snapshot:{token}` —
                 *  the person's unread @Inbox count at this house, the row in
                 *  `staff_unreads_snapshot` keyed by `staffUnreadsTopicId`.
                 *  Payload-carrying, like the bidder's unreads snapshot. */
                snapshot: (token: string) =>
                    `staff-user:${id}:tenant:${tenantId}:unreads:snapshot:${token}` as const,
            },
        }),
    }),
} as const;

/** The string literal type a topic builder returns. */
export type Topic = string;

/** What replaces the HMAC segment in a redacted admin topic. */
const REDACTED_TOKEN = '<redacted>';

/** The trailing 128-bit hex HMAC segment of a private `customer-user:` / `staff-user:` topic. */
const PRIVATE_TOPIC_TOKEN = /:[0-9a-f]{32}$/;

/**
 * Strip the HMAC token out of an `admin:` topic so the topic can be logged.
 *
 * **The token is a CREDENTIAL, not a name.** For every `admin:` topic above,
 * obscurity IS the access gate until the transport gains real per-subscriber
 * auth (ADR 042 §6, ADR 011 §5.2) — anyone who can name the full string can
 * subscribe to it and receive the confidential payloads those channels carry:
 * de-anonymized bidder identity (BL-1253), a buyer's stored proxy maximum
 * (BL-1325), and the BL-1252 clerk-only scalars. The client never derives it;
 * it learns the string from an authenticated mint response.
 *
 * So a raw admin topic in a server log is a bearer-token disclosure to everyone
 * who can read the log — a materially wider audience than the tenant-scoped
 * session the token gates. Server-side log lines therefore carry the redacted
 * form, which keeps every part that makes a log line useful (scope, entity, id,
 * aspect) and drops only the secret.
 *
 * A tokened `customer-user:` topic (BUG-1938) or `staff-user:` topic (BL-2491)
 * is redacted the same way. Every other topic contains no secret and is
 * returned unchanged.
 *
 * **What this does NOT cover, deliberately.** It redacts the topic a caller puts
 * in a log's structured context. It does not reach inside an `Error` logged
 * alongside that context — and `topicToChannel` hands the RAW topic to the
 * vendor SDK as the channel name, so a vendor whose error echoed the channel
 * into its own `message` / `stack` would republish the token through Cloud
 * Logging's promoted `error.*` fields. Checked rather than assumed: supabase-js's
 * `httpSend` derives its rejection message from `response.statusText` and the
 * server's error body, never the channel, so there is no live leak.
 *
 * It is left uncovered on purpose. Scrubbing `message` / `stack` before logging
 * means mutating the one artifact whose value is being verbatim, to defend a
 * behaviour no current vendor exhibits. If a vendor ever does start echoing the
 * channel, the fix belongs at that adapter, where the shape is known.
 *
 * @param topic - A topic string from a builder above.
 * @returns The topic with any trailing HMAC segment replaced.
 */
export function redactTopicSecret(topic: string): string {
    // BUG-1938: a bidder's private topic carries the same kind of credential.
    // Only its tokened form is redacted; a bare one holds no secret.
    // BL-2491: a staff member's private topic carries one too.
    const isPrivateUserTopic =
        (topic.startsWith('customer-user:') || topic.startsWith('staff-user:')) &&
        PRIVATE_TOPIC_TOKEN.test(topic);
    if (!topic.startsWith('admin:') && !isPrivateUserTopic) return topic;

    // Every admin builder puts the token LAST, so the secret is the final
    // segment and everything before it is safe, human-meaningful routing
    // information. The prefix test above guarantees a colon exists, so
    // `lastIndexOf` cannot return -1 here and needs no guard.
    return `${topic.slice(0, topic.lastIndexOf(':') + 1)}${REDACTED_TOKEN}`;
}
